import type { HexMapState } from '../../../engine/render/renderer';
import {
    ACTION__UI__DISMISS_ALERT,
    ACTION__UI__DISMISS_NOTIFICATION,
    ACTION__UI__OPEN_BATTLE_REPORT,
    ACTION__UI__OPEN_SCOUTING_MISSION,
} from '../../../engine/input/actionIds';
import { t } from '../../../i18n/translations';
import type { Severity } from './types';

export type DismissibleKind = 'notification' | 'alert';

export type DismissibleProgress = {
    current: number;
    total: number;
};

export type DismissibleAction = {
    id: string;
    label: string;
};

export interface Dismissible {
    id: string;
    kind: DismissibleKind;
    severity: Severity;
    title(lang: HexMapState['config']['ui']['language']): string;
    detail(lang: HexMapState['config']['ui']['language']): string;
    progress(): DismissibleProgress | null;
    action(): DismissibleAction | null;
    dismissActionId(): string;
}

export abstract class AbstractDismissible implements Dismissible {
    abstract id: string;
    abstract kind: DismissibleKind;
    abstract severity: Severity;
    abstract title(lang: HexMapState['config']['ui']['language']): string;
    abstract detail(lang: HexMapState['config']['ui']['language']): string;
    progress(): DismissibleProgress | null {
        return null;
    }

    action(): DismissibleAction | null {
        return null;
    }

    dismissActionId(): string {
        if (this.kind === 'notification') {
            return `${ACTION__UI__DISMISS_NOTIFICATION}:${this.id}`;
        }
        return `${ACTION__UI__DISMISS_ALERT}:${this.id}`;
    }
}

export class NotificationDismissible extends AbstractDismissible {
    kind: DismissibleKind = 'notification';
    severity: Severity = 'info';
    constructor(
        public id: string,
        private titleText: string,
        private detailText: string,
        private progressData?: DismissibleProgress,
        private actionData?: DismissibleAction | null,
    ) {
        super();
    }

    title(_lang: HexMapState['config']['ui']['language']): string {
        return this.titleText;
    }

    detail(_lang: HexMapState['config']['ui']['language']): string {
        return this.detailText;
    }

    progress(): DismissibleProgress | null {
        return this.progressData ?? null;
    }

    action(): DismissibleAction | null {
        return this.actionData ?? null;
    }
}

export class AlertDismissible extends AbstractDismissible {
    kind: DismissibleKind = 'alert';
    constructor(
        public id: string,
        private message: string,
        public severity: Severity,
    ) {
        super();
    }

    title(_lang: HexMapState['config']['ui']['language']): string {
        return '';
    }

    detail(_lang: HexMapState['config']['ui']['language']): string {
        return this.message;
    }
}

export function buildNotificationDismissibles(
    state: HexMapState,
    lang: HexMapState['config']['ui']['language'],
): NotificationDismissible[] {
    const notifications: NotificationDismissible[] = [];

    state.foundingExpeditions.forEach((expedition) => {
        const targetLabel = parseTileLabel(expedition.targetTileId);
        if (expedition.status === 'traveling') {
            notifications.push(
                new NotificationDismissible(
                    `founding:${expedition.id}`,
                    t(lang, 'overlay.noticeFounding', targetLabel),
                    `${expedition.progress}/${expedition.totalTicks}`,
                    { current: expedition.progress, total: expedition.totalTicks },
                ),
            );
        } else if (expedition.status === 'failed') {
            const reason = expedition.failedBy
                ? t(lang, `enemy.${expedition.failedBy}`)
                : t(lang, 'overlay.noticeFoundingFailedUnknown');
            notifications.push(
                new NotificationDismissible(
                    `founding_failed:${expedition.id}:${expedition.completedAt ?? 'x'}`,
                    t(lang, 'overlay.noticeFoundingFailed', targetLabel),
                    t(lang, 'overlay.noticeFoundingFailedDetail', reason),
                ),
            );
        } else if (expedition.completedAt !== undefined) {
            notifications.push(
                new NotificationDismissible(
                    `founding:${expedition.id}:${expedition.completedAt}`,
                    t(lang, 'overlay.noticeFoundingDone', targetLabel),
                    t(lang, 'overlay.noticeDismiss'),
                ),
            );
        }
    });

    state.transportRoutes.forEach((route) => {
        const from = state.settlementsById[route.fromSettlementId];
        const to = state.settlementsById[route.toSettlementId];
        const label = `${from?.name || route.fromSettlementId} → ${to?.name || route.toSettlementId}`;
        if (route.status === 'traveling') {
            notifications.push(
                new NotificationDismissible(
                    `transport:${route.id}`,
                    t(lang, 'overlay.noticeTransport', label),
                    `${route.progress}/${route.totalTicks}`,
                    { current: route.progress, total: route.totalTicks },
                ),
            );
        } else if (route.lastArrivedAt !== undefined) {
            notifications.push(
                new NotificationDismissible(
                    `transport:${route.id}:${route.lastArrivedAt}`,
                    t(lang, 'overlay.noticeTransportArrived', label),
                    t(lang, 'overlay.noticeDismiss'),
                ),
            );
        }
    });

    state.scoutingMissions.forEach((mission) => {
        const targetLabel = parseTileLabel(mission.targetTileId);
        if (mission.status === 'traveling') {
            notifications.push(
                new NotificationDismissible(
                    `scout:${mission.id}`,
                    t(lang, 'overlay.noticeScouting', targetLabel),
                    `${mission.progress}/${mission.travelTicks}`,
                    { current: mission.progress, total: mission.travelTicks },
                    {
                        id: `${ACTION__UI__OPEN_SCOUTING_MISSION}:${mission.id}`,
                        label: t(lang, 'overlay.noticeScoutingDetails'),
                    },
                ),
            );
            return;
        }
        if (mission.status === 'battle' && mission.battle) {
            const totalTicks = mission.battle.totalRounds * mission.battle.ticksPerRound;
            const currentTicks =
                mission.battle.roundIndex * mission.battle.ticksPerRound + mission.battle.roundTick;
            notifications.push(
                new NotificationDismissible(
                    `battle:${mission.id}`,
                    t(lang, 'overlay.noticeBattle', targetLabel),
                    `${currentTicks}/${totalTicks}`,
                    { current: currentTicks, total: Math.max(1, totalTicks) },
                    {
                        id: `${ACTION__UI__OPEN_SCOUTING_MISSION}:${mission.id}`,
                        label: t(lang, 'overlay.noticeScoutingDetails'),
                    },
                ),
            );
            return;
        }
        if (mission.status === 'holding') {
            notifications.push(
                new NotificationDismissible(
                    `battle_done:${mission.id}`,
                    t(lang, 'overlay.noticeBattleDone', targetLabel),
                    t(lang, 'overlay.noticeAwaitingOrders'),
                    undefined,
                    {
                        id: `${ACTION__UI__OPEN_SCOUTING_MISSION}:${mission.id}`,
                        label: t(lang, 'overlay.noticeScoutingDetails'),
                    },
                ),
            );
            return;
        }
        if (mission.status === 'returning') {
            notifications.push(
                new NotificationDismissible(
                    `scout:return:${mission.id}`,
                    t(lang, 'overlay.noticeScoutingReturn', targetLabel),
                    `${mission.progress}/${mission.travelTicks}`,
                    { current: mission.progress, total: mission.travelTicks },
                    {
                        id: `${ACTION__UI__OPEN_SCOUTING_MISSION}:${mission.id}`,
                        label: t(lang, 'overlay.noticeScoutingDetails'),
                    },
                ),
            );
            return;
        }
        if (mission.completedAt !== undefined) {
            notifications.push(
                new NotificationDismissible(
                    `scout:done:${mission.id}:${mission.completedAt}`,
                    t(lang, 'overlay.noticeScoutingDone', targetLabel),
                    t(lang, 'overlay.noticeDismiss'),
                ),
            );
        }
    });

    state.battleReports.forEach((report) => {
        const targetLabel = parseTileLabel(report.tileId);
        const resultLabel = t(lang, `battle.result.${report.outcome.winner}`);
        notifications.push(
            new NotificationDismissible(
                `battle_report:${report.id}`,
                t(lang, 'overlay.noticeBattleReport', targetLabel),
                t(
                    lang,
                    'overlay.noticeBattleReportDetail',
                    resultLabel,
                    report.outcome.attackerLosses,
                    report.outcome.defenderLosses,
                ),
                undefined,
                {
                    id: `${ACTION__UI__OPEN_BATTLE_REPORT}:${report.id}`,
                    label: t(lang, 'overlay.noticeBattleDetails'),
                },
            ),
        );
    });

    return notifications;
}

export function buildAlertDismissibles(state: HexMapState): AlertDismissible[] {
    return state.alerts.map((alert) => new AlertDismissible(alert.id, alert.message, alert.level));
}

function parseTileLabel(tileId: string): string {
    const parts = tileId.split('_');
    if (parts.length >= 4) {
        const q = Number(parts[1]);
        const r = Number(parts[2]);
        if (Number.isFinite(q) && Number.isFinite(r)) {
            return `${q},${r}`;
        }
    }
    return tileId;
}

export function getDismissibleProgress(dismissible: Dismissible): DismissibleProgress | null {
    return dismissible.progress();
}

export function getDismissibleDetail(
    dismissible: Dismissible,
    lang: HexMapState['config']['ui']['language'],
): string {
    return dismissible.detail(lang);
}

export function getDismissibleTitle(
    dismissible: Dismissible,
    lang: HexMapState['config']['ui']['language'],
): string {
    return dismissible.title(lang);
}
